import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Page } from '@/shell/AppShell'
import { Chip } from '@/components/forge/Chip'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { ForgeSelect } from '@/components/forge/ForgeSelect'
import { Icon } from '@/components/forge/Icon'
import { PlaceFilter } from '@/components/forge/PlaceFilter'
import { EmptyState, ErrorState, SkeletonRows } from '@/components/forge/States'
import { TablePagination } from '@/components/forge/TablePagination'
import { Num } from '@/components/forge/primitives'
import { CHART_TIERS } from '@/features/sensorCharts/api'
import { useSensorChartAccess, useSensorCharts } from '@/features/sensorCharts/queries'
import { ROOT_ID } from '@/features/hierarchy/api'
import { useHierarchyAncestors } from '@/features/hierarchy/queries'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { formatInteger } from '@/lib/format'
import { cn } from '@/lib/cn'
import { ChartCard } from './SensorChartParts'

/**
 * The Sensor Explorer: every saved sensor chart, filtered. LIVE —
 * `GET /sensors/charts`, at `/app/sensors`.
 *
 * This replaced two screens. The demo Explorer's grid of channel cards was
 * fixtures from end to end — authored channels, bands, states and traces —
 * and the demo chart catalogue carried a worst-state and an author the API
 * does not serve. Both are unrouted and kept (`SensorExplorerPage`,
 * `ChartCatalogPage`). `/app/sensors/charts` now redirects here.
 *
 * ── Every filter the list takes, and nothing it does not ──────────────────
 *  - search — the server's `name ILIKE`;
 *  - location — a plant, line or machine and everything beneath it, through
 *    the same `HierarchyPicker` the anomaly list and the top bar use;
 *  - sort — name or creation time, either direction;
 *  - page and page size.
 * The list's `plant_id`/`line_id`/`machine_id` params are not offered as an
 * "exactly here" option: the server compares them with the chart's resolved
 * path, so they return the same subtree (see `fetchSensorCharts`).
 *
 * ── The filters are the URL ───────────────────────────────────────────────
 * `?q=&place=line:<id>&sort=name:asc&page=&size=`, so a filtered view can be
 * sent to somebody and Back undoes a filter. The place's name and trail are not
 * in the URL; they are read back from the hierarchy (`useHierarchyAncestors`)
 * on a cold link, and taken from the picker's own answer otherwise.
 */

/** The sort select's options: field and direction as one choice. */
const SORTS = ['created_at:desc', 'created_at:asc', 'name:asc', 'name:desc']
const DEFAULT_SORT = 'created_at:desc'
const DEFAULT_SIZE = 24

export function SensorChartsPage() {
  const { t } = useTranslation(['app', 'common'])
  const access = useSensorChartAccess()
  const [searchParams, setSearchParams] = useSearchParams()

  /* ── Read the URL ── */
  const q = searchParams.get('q') ?? ''
  const sort = SORTS.includes(searchParams.get('sort')) ? searchParams.get('sort') : DEFAULT_SORT
  const [sortBy, sortDir] = sort.split(':')
  const page = Math.max(1, Number.parseInt(searchParams.get('page') ?? '1', 10) || 1)
  const pageSize = Number.parseInt(searchParams.get('size') ?? '', 10) || DEFAULT_SIZE
  const placeTarget = useMemo(() => {
    const [tier, id] = (searchParams.get('place') ?? '').split(':')
    return CHART_TIERS.has(tier) && id ? { tier, id } : null
  }, [searchParams])

  const update = (changes, { keepPage = false, replace = false } = {}) =>
    setSearchParams((current) => {
      const url = new URLSearchParams(current)
      for (const [key, value] of Object.entries(changes)) {
        if (value) url.set(key, value)
        else url.delete(key)
      }
      // Any change of filter is page one; only paging itself keeps the page.
      if (!keepPage) url.delete('page')
      return url
    }, { replace })

  /* The search box is typed into locally and committed to the URL as it
     settles, so each keystroke is not a history entry and a request. */
  const [typed, setTyped] = useState(q)
  const deferred = useDeferredValue(typed)
  useEffect(() => {
    if (deferred.trim() !== q.trim()) update({ q: deferred.trim() }, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deferred])

  /* ── The place: picked this session, or resolved from a cold link ── */
  const [picked, setPicked] = useState(null)
  const fromPicker = picked && placeTarget && picked.id === placeTarget.id ? picked : null
  const ancestors = useHierarchyAncestors(placeTarget, { enabled: Boolean(placeTarget) && !fromPicker })
  const place = useMemo(() => {
    if (!placeTarget) return null
    if (fromPicker) return fromPicker
    const path = ancestors.data
    const node = path?.at(-1)
    if (!node || node.id !== placeTarget.id) return { ...placeTarget, name: '…', trail: undefined }
    const root = { id: ROOT_ID, tier: 'enterprise', parentId: null, name: t('placeFilter.all'), code: null, isActive: true }
    return { id: node.id, tier: node.tier, name: node.name, trail: [root, ...path.slice(0, -1)] }
  }, [placeTarget, fromPicker, ancestors.data, t])

  const choosePlace = (next) => {
    setPicked(next)
    update({ place: next ? `${next.tier}:${next.id}` : '' })
  }

  const charts = useSensorCharts(
    { search: q, place: placeTarget, sortBy, sortDir, page, pageSize },
    { enabled: access.charts },
  )

  useDocumentTitle(`${t('sensors.title')} · ${t('common:brand.name')}`)

  const items = charts.data?.items ?? []
  const total = charts.data?.total ?? 0
  const filtered = Boolean(q || placeTarget)

  const clearAll = () => {
    setTyped('')
    setPicked(null)
    update({ q: '', place: '' })
  }

  return (
    <Page>
      <header className="mb-4 flex flex-wrap items-end gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="m-0 text-display text-balance">{t('sensors.title')}</h1>
          <p className="mt-2 mb-0 text-body text-ink-dim">
            <Num>
              {filtered
                ? t('sensors.charts.subtitleFiltered', { total: formatInteger(total), count: total })
                : t('sensors.charts.subtitle', { total: formatInteger(total), count: total })}
            </Num>
          </p>
        </div>
        {access.charts ? (
          <div className="flex flex-wrap gap-2">
            <ForgeButton onClick={() => charts.refetch()} disabled={charts.isFetching}>
              <Icon name="rail" size="xs" />
              {charts.isFetching && !charts.isPending ? t('sensors.charts.refreshing') : t('sensors.charts.refresh')}
            </ForgeButton>
            <ForgeButton asChild variant="primary">
              <Link to="/app/sensors/charts/new">
                <Icon name="plus" size="xs" />
                {t('sensors.workstation.newChart')}
              </Link>
            </ForgeButton>
          </div>
        ) : null}
      </header>

      {access.isPending ? (
        <SkeletonRows count={4} />
      ) : !access.charts ? (
        <EmptyState icon="lock" title={t('sensors.charts.noAccess')} body={t('sensors.charts.noAccessBody')} />
      ) : (
        <>
          {/* Filters. Search · location · sort, then what is in force. */}
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <label className="flex min-w-[14rem] flex-1 items-center gap-2 rounded-sm border border-hairline bg-card px-3 py-2 focus-within:border-forge sm:max-w-[24rem]">
              <Icon name="search" size="sm" className="shrink-0 text-ink-mute" />
              <span className="sr-only">{t('sensors.charts.search')}</span>
              <input
                value={typed}
                onChange={(event) => setTyped(event.target.value)}
                placeholder={t('sensors.charts.searchPlaceholder')}
                className="min-w-0 flex-1 bg-transparent text-body-sm text-ink outline-none placeholder:text-ink-mute"
              />
            </label>

            <PlaceFilter
              id="charts-place"
              value={place}
              onChange={choosePlace}
              title={t('sensors.charts.filterPlaceTitle')}
              note={t('sensors.charts.filterPlaceNote')}
            />

            <ForgeSelect
              label={t('sensors.charts.sortLabel')}
              labelInline
              value={sort}
              onValueChange={(next) => update({ sort: next === DEFAULT_SORT ? '' : next })}
              options={SORTS.map((value) => ({ value, label: t(`sensors.charts.sort.${value.replace(':', '_')}`) }))}
            />
          </div>

          {filtered ? (
            <div className="mb-3.5 flex flex-wrap items-center gap-2">
              {q ? (
                <Chip
                  selected
                  onClick={() => {
                    setTyped('')
                    update({ q: '' })
                  }}
                  aria-label={t('sensors.charts.removeFilter', { filter: q })}
                >
                  {t('sensors.charts.chipSearch', { q })}
                  <Icon name="x" size="xs" />
                </Chip>
              ) : null}
              {place ? (
                <Chip selected onClick={() => choosePlace(null)} aria-label={t('sensors.charts.removeFilter', { filter: place.name })}>
                  {t('sensors.charts.chipPlace', { place: place.name })}
                  <Icon name="x" size="xs" />
                </Chip>
              ) : null}
              <button
                type="button"
                onClick={clearAll}
                className="cursor-pointer text-body-sm text-ink-dim underline decoration-from-font hover:text-ink"
              >
                {t('sensors.clearAll')}
              </button>
            </div>
          ) : null}

          {charts.isError ? (
            <ErrorState error={charts.error} title={t('sensors.charts.loadFailed')} onRetry={() => charts.refetch()} />
          ) : charts.isPending ? (
            <SkeletonRows count={4} />
          ) : items.length === 0 ? (
            filtered ? (
              <EmptyState
                icon="search"
                title={t('sensors.charts.noMatches')}
                body={t('sensors.charts.noMatchesBody')}
                action={<ForgeButton onClick={clearAll}>{t('sensors.clearAll')}</ForgeButton>}
              />
            ) : (
              <EmptyState
                icon="chart"
                title={t('sensors.workstation.emptyTitle')}
                body={t('sensors.charts.emptyBody')}
                action={
                  <ForgeButton asChild variant="primary">
                    <Link to="/app/sensors/charts/new">
                      <Icon name="plus" size="xs" />
                      {t('sensors.charts.create')}
                    </Link>
                  </ForgeButton>
                }
              />
            )
          ) : (
            <>
              <ul
                className={cn(
                  'm-0 grid list-none gap-3.5 p-0 [grid-template-columns:repeat(auto-fill,minmax(min(100%,24rem),1fr))]',
                  charts.isPlaceholderData && 'opacity-60',
                )}
              >
                {items.map((chart) => (
                  <li key={chart.id}>
                    <ChartCard chart={chart} />
                  </li>
                ))}
              </ul>
              <TablePagination
                className="mt-3.5"
                total={total}
                page={page}
                pageSize={pageSize}
                onPageChange={(next) => update({ page: next > 1 ? String(next) : '' }, { keepPage: true })}
                onPageSizeChange={(size) => update({ size: size === DEFAULT_SIZE ? '' : String(size) })}
                label={t('sensors.charts.title')}
              />
            </>
          )}
        </>
      )}
    </Page>
  )
}
